/**
 * HUD flotante arrastrable durante la grabación.
 * Acceso rápido a pausar / finalizar y a abrir el panel completo.
 */
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import {
  mdiMicrophone,
  mdiPause,
  mdiPlay,
  mdiStop,
  mdiDockRight,
  mdiDragHorizontalVariant,
} from '@mdi/js'
import VoiceMeter from './VoiceMeter'
import '../skins/voice.css'

const POS_KEY = 'udv.voiceHud.pos'

function formatDuration(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r < 10 ? '0' : ''}${r}`
}

function loadSavedPos() {
  try {
    const raw = window.localStorage.getItem(POS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw)
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return { x: p.x, y: p.y }
  } catch (e) {
    /* ignore */
  }
  return null
}

function defaultPos(bottomInset) {
  if (typeof window === 'undefined') return { x: 24, y: 24 }
  const w = Math.min(380, window.innerWidth - 24)
  const x = Math.max(12, (window.innerWidth - w) / 2)
  const y = Math.max(
    12,
    window.innerHeight - Math.max(12, bottomInset) - 64
  )
  return { x, y }
}

function clampPos(x, y, el) {
  const rect = el ? el.getBoundingClientRect() : { width: 320, height: 52 }
  const maxX = Math.max(8, window.innerWidth - rect.width - 8)
  const maxY = Math.max(8, window.innerHeight - rect.height - 8)
  return {
    x: Math.min(maxX, Math.max(8, x)),
    y: Math.min(maxY, Math.max(8, y)),
  }
}

/**
 * @param {object} props
 */
function VoiceRecHud({
  visible,
  skin = 'classic',
  recording = false,
  paused = false,
  busy = false,
  canPause = true,
  stream = null,
  elapsedMs = 0,
  liveTranscript = null,
  sttRuntime = null,
  panelOpen = false,
  bottomInset = 16,
  onPause,
  onResume,
  onStop,
  onOpenPanel,
}) {
  const [tick, setTick] = useState(0)
  const rootRef = useRef(null)
  const dragRef = useRef({
    active: false,
    moved: false,
    ox: 0,
    oy: 0,
    sx: 0,
    sy: 0,
  })
  const [pos, setPos] = useState(() => loadSavedPos() || defaultPos(bottomInset))

  useEffect(() => {
    if (!visible || !recording || paused) return undefined
    const id = setInterval(() => setTick((n) => n + 1), 250)
    return () => clearInterval(id)
  }, [visible, recording, paused])

  useEffect(() => {
    if (!visible) return undefined
    const onResize = () => {
      setPos((p) => clampPos(p.x, p.y, rootRef.current))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [visible])

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current
      if (!d.active) return
      const cx = e.touches ? e.touches[0].clientX : e.clientX
      const cy = e.touches ? e.touches[0].clientY : e.clientY
      const dx = cx - d.sx
      const dy = cy - d.sy
      if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true
      const next = clampPos(d.ox + dx, d.oy + dy, rootRef.current)
      setPos(next)
      if (e.cancelable) e.preventDefault()
    }
    const onUp = () => {
      const d = dragRef.current
      if (!d.active) return
      d.active = false
      setPos((p) => {
        const c = clampPos(p.x, p.y, rootRef.current)
        try {
          window.localStorage.setItem(POS_KEY, JSON.stringify(c))
        } catch (err) {
          /* ignore */
        }
        return c
      })
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onUp)
    }
  }, [])

  if (!visible || (!recording && !busy)) return null
  void tick

  const backendLabel =
    (sttRuntime && sttRuntime.npuActive && 'NPU') ||
    (sttRuntime && sttRuntime.backendLabel) ||
    'WASM'
  const isInferring =
    liveTranscript && liveTranscript.vadState === 'inferring'
  const liveModelTier =
    (sttRuntime &&
      sttRuntime.modelsWarm &&
      sttRuntime.modelsWarm[0] &&
      String(sttRuntime.modelsWarm[0]).match(/whisper-([\w.]+)/i)?.[1]) ||
    'tiny'

  const beginDrag = (e) => {
    if (e.button != null && e.button !== 0) return
    const cx = e.touches ? e.touches[0].clientX : e.clientX
    const cy = e.touches ? e.touches[0].clientY : e.clientY
    dragRef.current = {
      active: true,
      moved: false,
      ox: pos.x,
      oy: pos.y,
      sx: cx,
      sy: cy,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
  }

  const onExpandClick = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      dragRef.current.moved = false
      return
    }
    if (onOpenPanel) onOpenPanel()
  }

  const body = (
    <div
      ref={rootRef}
      className={`udv-voice-hud is-draggable skin-${skin}${paused ? ' is-paused' : ''}${
        panelOpen ? ' is-panel-open' : ''
      } vad-${
        (liveTranscript && liveTranscript.vadState) ||
        (recording ? 'listening' : 'idle')
      }`}
      style={{
        left: pos.x,
        top: pos.y,
        right: 'auto',
        bottom: 'auto',
        transform: 'none',
      }}
      role="toolbar"
      aria-label="Controles rápidos de grabación"
    >
      <button
        type="button"
        className="udv-voice-hud-drag"
        title="Arrastrar"
        aria-label="Arrastrar panel"
        onPointerDown={beginDrag}
        onTouchStart={beginDrag}
      >
        <Icon path={mdiDragHorizontalVariant} size={0.75} color="currentColor" />
      </button>

      <button
        type="button"
        className="udv-voice-hud-expand"
        onPointerDown={beginDrag}
        onClick={onExpandClick}
        title={panelOpen ? 'Panel de notas abierto' : 'Abrir panel de notas'}
      >
        <span className={`udv-voice-hud-dot${recording && !paused ? ' is-live' : ''}`} />
        <div className="udv-voice-hud-meter">
          <VoiceMeter
            stream={stream}
            active={!!recording && !!stream}
            paused={!!paused}
            height={34}
            variant="mini"
          />
        </div>
        <span className="udv-voice-hud-time">{formatDuration(elapsedMs)}</span>
        {recording ? (
          <span
            className={`udv-voice-hud-stt-pill${
              sttRuntime && sttRuntime.npuActive ? ' is-npu' : ''
            }${isInferring ? ' is-inferring' : ''}`}
            title={`Whisper ${liveModelTier} · ${backendLabel}`}
          >
            {isInferring ? '… ' : ''}
            {liveModelTier} · {backendLabel}
          </span>
        ) : null}
        <Icon path={mdiDockRight} size={0.7} color="currentColor" />
      </button>

      <div className="udv-voice-hud-actions">
        {canPause ? (
          <button
            type="button"
            className="udv-voice-hud-btn"
            disabled={busy || !recording}
            onClick={paused ? onResume : onPause}
            title={paused ? 'Continuar' : 'Pausar'}
            aria-label={paused ? 'Continuar' : 'Pausar'}
          >
            <Icon path={paused ? mdiPlay : mdiPause} size={0.85} color="currentColor" />
          </button>
        ) : null}
        <button
          type="button"
          className="udv-voice-hud-btn danger"
          disabled={busy || !recording}
          onClick={onStop}
          title="Finalizar y guardar"
          aria-label="Finalizar"
        >
          <Icon path={mdiStop} size={0.85} color="currentColor" />
        </button>
        <button
          type="button"
          className="udv-voice-hud-btn ghost"
          disabled={busy}
          onClick={onOpenPanel}
          title="Abrir panel completo"
          aria-label="Panel completo"
        >
          <Icon path={mdiMicrophone} size={0.8} color="currentColor" />
        </button>
      </div>
      {recording ? (
        <p
          className={`udv-voice-hud-live vad-${
            (liveTranscript && liveTranscript.vadState) ||
            (liveTranscript && liveTranscript.status) ||
            'idle'
          }${
            liveTranscript && liveTranscript.status === 'error'
              ? ' is-error'
              : ''
          }${
            liveTranscript &&
            (liveTranscript.finalText || liveTranscript.interimText || liveTranscript.text)
              ? ' has-text'
              : ''
          }`}
          title={
            (liveTranscript &&
              (liveTranscript.text || liveTranscript.error)) ||
            ''
          }
        >
          {liveTranscript && liveTranscript.status === 'error' ? (
            <span className="udv-voice-live-interim">
              {liveTranscript.error || 'Dictado no disponible'}
            </span>
          ) : liveTranscript &&
            (liveTranscript.finalText || liveTranscript.interimText) ? (
            <>
              <span className="udv-voice-live-final">
                {liveTranscript.finalText}
              </span>
              {liveTranscript.interimText ? (
                <span className="udv-voice-live-interim">
                  {' '}
                  {liveTranscript.interimText}
                </span>
              ) : null}
            </>
          ) : liveTranscript && liveTranscript.text ? (
            <span className="udv-voice-live-final">{liveTranscript.text}</span>
          ) : (
            <span className="udv-voice-live-interim">
              {liveTranscript && liveTranscript.vadState === 'speaking'
                ? 'Escuchando voz…'
                : liveTranscript && liveTranscript.vadState === 'inferring'
                  ? 'Transcribiendo…'
                  : 'Escuchando… (local Whisper)'}
            </span>
          )}
        </p>
      ) : null}
    </div>
  )

  return createPortal(body, document.body)
}

export default VoiceRecHud
